/**
 * Multi-track lane: one row per track, free placement, edge trim, snap.
 */

import { useEffect, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { VIDEO_MIN_CLIP_MS, type TimelineClip, type TimelineTrack, type TransitionId } from '@canvas/shared-types';
import { formatTimecode } from '@/lib/video/sceneTimeline';
import {
  applyClipTransition,
  closeGap,
  duplicateClip,
  gapAt,
  moveClip,
  pasteClip,
  removeClip,
  renameClip,
  setClipDuration,
  splitClip,
  TRACK,
  transitionBlockReason,
  transitionHoldMs,
  trimClip,
  ungroupClip,
} from '@/lib/video/timelineEdit';
import { transitionDef } from '@/data/transitions';
import { useVideoStore } from '@/stores/videoStore';
import Playhead from './Playhead';
import TimelineRuler from './TimelineRuler';

const LANE_H = 36;

interface ClipMenuState {
  x: number;
  y: number;
  clipId: string;
  panel: 'actions' | 'rename' | 'duration';
}

interface DragState {
  id: string;
  ids: Set<string>;
  mode: 'move' | 'start' | 'end';
  deltaMs: number;
  trackId: string;
  originStart: number;
  originDuration: number;
}

function laneOrder(tracks: TimelineTrack[]): TimelineTrack[] {
  const rank: Record<TimelineTrack['kind'], number> = { text: 0, caption: 1, visual: 2, audio: 3 };
  return tracks.slice().sort((a, b) => rank[a.kind] - rank[b.kind] || b.index - a.index);
}

export default function ClipTimeline(): JSX.Element | null {
  const video = useVideoStore((s) => s.video);
  const pxPerSec = useVideoStore((s) => s.pxPerSec);
  const currentTimeMs = useVideoStore((s) => s.currentTimeMs);
  const selectedId = useVideoStore((s) => s.selectedClipId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const suppressSeek = useRef(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [gapPick, setGapPick] = useState<{ trackId: string; ms: number } | null>(null);
  const [menu, setMenu] = useState<ClipMenuState | null>(null);
  const [copied, setCopied] = useState<TimelineClip | null>(null);

  useEffect(() => {
    if (!menu) return;
    const close = (): void => setMenu(null);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const deleteSelected = (): void => {
    if (!selectedId) return;
    useVideoStore.getState().changeTimeline((doc) => removeClip(doc, selectedId));
    useVideoStore.getState().selectClip(null);
    setMenu(null);
  };

  if (!video || video.v !== 2 || !video.tracks || !video.clips) return null;
  const tracks = laneOrder(video.tracks);
  const liveGap = gapPick ? gapAt(video.clips, gapPick.trackId, gapPick.ms) : null;
  const totalMs = Math.max(video.totalDurationMs, 8000);
  const width = (totalMs / 1000) * pxPerSec + 160;
  const playheadX = (currentTimeMs / 1000) * pxPerSec;

  const display = (clip: TimelineClip): TimelineClip => {
    if (!drag || !drag.ids.has(clip.id)) return clip;
    if (drag.mode === 'move') {
      return {
        ...clip,
        startMs: Math.max(0, clip.startMs + drag.deltaMs),
        trackId: clip.id === drag.id ? drag.trackId : clip.trackId,
      };
    }
    if (clip.id !== drag.id) return clip;
    if (drag.mode === 'start') {
      const start = Math.max(0, Math.min(drag.originStart + drag.originDuration - 100, drag.originStart + drag.deltaMs));
      return { ...clip, startMs: start, durationMs: drag.originStart + drag.originDuration - start };
    }
    return { ...clip, durationMs: Math.max(100, drag.originDuration + drag.deltaMs) };
  };

  const trackUnder = (x: number, y: number, clip: TimelineClip): string | undefined => {
    const lane = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-track-id]');
    const trackId = lane?.dataset.trackId;
    if (!trackId || clip.groupId) return undefined;
    const kind = video.tracks?.find((track) => track.id === trackId)?.kind;
    const fits =
      (clip.kind === 'audio' && kind === 'audio') ||
      (clip.kind === 'text' && kind === 'text') ||
      (clip.kind === 'caption' && kind === 'caption') ||
      (clip.kind !== 'audio' && clip.kind !== 'text' && clip.kind !== 'caption' && kind === 'visual');
    return fits ? trackId : undefined;
  };

  const openMenu = (x: number, y: number, clip: TimelineClip): void => {
    const pad = 8;
    const menuW = 240;
    const menuH = 300;
    setGapPick(null);
    useVideoStore.getState().selectClip(clip.id);
    setMenu({
      x: Math.max(pad, Math.min(x, window.innerWidth - menuW - pad)),
      y: Math.max(pad, Math.min(y, window.innerHeight - menuH - pad)),
      clipId: clip.id,
      panel: 'actions',
    });
  };

  const selectCreated = (before: ReadonlySet<string>): void => {
    const created = useVideoStore.getState().video?.clips?.find((item) => !before.has(item.id));
    if (created) useVideoStore.getState().selectClip(created.id);
  };

  const duplicateSelected = (id: string): void => {
    const before = new Set(useVideoStore.getState().video?.clips?.map((item) => item.id) ?? []);
    useVideoStore.getState().changeTimeline((doc) => duplicateClip(doc, id));
    selectCreated(before);
    setMenu(null);
  };

  const copyClip = (clip: TimelineClip): void => {
    setCopied(JSON.parse(JSON.stringify(clip)) as TimelineClip);
    setMenu(null);
  };

  const pasteCopied = (): void => {
    if (!copied) return;
    const before = new Set(useVideoStore.getState().video?.clips?.map((item) => item.id) ?? []);
    const at = useVideoStore.getState().currentTimeMs;
    useVideoStore.getState().changeTimeline((doc) => pasteClip(doc, copied, at));
    selectCreated(before);
    setMenu(null);
  };

  const splitSelected = (id: string): void => {
    const store = useVideoStore.getState();
    const clip = store.video?.clips?.find((item) => item.id === id);
    const at = store.currentTimeMs;
    const inside = clip && at > clip.startMs + VIDEO_MIN_CLIP_MS && at < clip.startMs + clip.durationMs - VIDEO_MIN_CLIP_MS;
    if (!inside) {
      store.setNotice('Move the playhead inside the clip, leaving a little on each side.');
      return;
    }
    store.setNotice(null);
    store.changeTimeline((doc) => splitClip(doc, id, at));
    setMenu(null);
  };

  const beginDrag = (event: ReactPointerEvent<HTMLElement>, clip: TimelineClip, forced?: 'start' | 'end'): void => {
    if (event.button !== 0) {
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const host = event.currentTarget.closest('.vt-clip') ?? event.currentTarget;
    const rect = host.getBoundingClientRect();
    const local = event.clientX - rect.left;
    const edge = event.pointerType === 'mouse' ? 10 : 22;
    const mode = forced ?? (local < edge ? 'start' : rect.width - local < edge ? 'end' : 'move');
    const group = clip.groupId
      ? new Set((video.clips ?? []).filter((item) => item.groupId === clip.groupId).map((item) => item.id))
      : new Set([clip.id]);
    setGapPick(null);
    setMenu(null);
    useVideoStore.getState().selectClip(clip.id);
    const originX = event.clientX;
    const originY = event.clientY;
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize';
    let hold: number | null = null;
    let menuOpened = false;
    const clearHold = (): void => {
      if (hold == null) return;
      window.clearTimeout(hold);
      hold = null;
    };
    const onMove = (ev: PointerEvent): void => {
      ev.preventDefault();
      if (Math.hypot(ev.clientX - originX, ev.clientY - originY) > 8) clearHold();
      const delta = ((ev.clientX - originX) / pxPerSec) * 1000;
      const trackId = mode === 'move' ? trackUnder(ev.clientX, ev.clientY, clip) ?? clip.trackId : clip.trackId;
      setDrag((current) => (current ? { ...current, deltaMs: delta, trackId } : current));
    };
    const finish = (ev: PointerEvent): void => {
      clearHold();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      document.body.style.cursor = previousCursor;
      if (menuOpened) {
        const swallow = (click: Event): void => {
          click.preventDefault();
          click.stopPropagation();
          window.removeEventListener('click', swallow, true);
        };
        window.addEventListener('click', swallow, true);
        window.setTimeout(() => window.removeEventListener('click', swallow, true), 600);
        return;
      }
      const delta = ((ev.clientX - originX) / pxPerSec) * 1000;
      const store = useVideoStore.getState();
      const playhead = store.currentTimeMs;
      const zoom = store.pxPerSec;
      const trackId = mode === 'move' ? trackUnder(ev.clientX, ev.clientY, clip) : undefined;
      const moved = Math.abs(delta) >= 2 || Boolean(trackId && trackId !== clip.trackId);
      if (moved) suppressSeek.current = true;
      if (!moved) {
        setDrag(null);
        return;
      }
      store.checkpoint();
      if (mode === 'move') {
        store.changeTimeline(
          (doc) => moveClip(doc, clip.id, clip.startMs + delta, trackId, zoom, playhead),
          { record: false }
        );
      } else if (mode === 'start') {
        store.changeTimeline((doc) => trimClip(doc, clip.id, 'start', clip.startMs + delta, zoom, playhead), { record: false });
      } else {
        store.changeTimeline(
          (doc) => trimClip(doc, clip.id, 'end', clip.startMs + clip.durationMs + delta, zoom, playhead),
          { record: false }
        );
      }
      setDrag(null);
    };
    if (event.pointerType !== 'mouse' && mode === 'move') {
      hold = window.setTimeout(() => {
        menuOpened = true;
        hold = null;
        document.body.style.cursor = previousCursor;
        setDrag(null);
        window.removeEventListener('pointermove', onMove);
        openMenu(originX, originY - 8, clip);
      }, 520);
    }
    setDrag({
      id: clip.id,
      ids: mode === 'move' ? group : new Set([clip.id]),
      mode,
      deltaMs: 0,
      trackId: clip.trackId,
      originStart: clip.startMs,
      originDuration: clip.durationMs,
    });
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  const applyTransitionTo = (clipId: string, transitionId: string): void => {
    const store = useVideoStore.getState();
    store.selectClip(clipId);
    const blocked = transitionId !== 'cut' && transitionId !== 'none' && store.video
      ? transitionBlockReason(store.video, clipId)
      : null;
    if (blocked) {
      store.setNotice(blocked);
      return;
    }
    const id = transitionId as TransitionId;
    const def = transitionDef(id);
    store.pause();
    store.changeTimeline((doc) =>
      applyClipTransition(doc, clipId, { id, durationMs: def.defaultDurationMs ?? 500 })
    );
    const hold = store.video ? transitionHoldMs(store.video, clipId) : null;
    if (hold != null) store.seek(hold);
    store.setNotice(null);
  };

  const onClipDrop = (event: ReactDragEvent<HTMLButtonElement>, clip: TimelineClip): void => {
    event.preventDefault();
    event.stopPropagation();
    const raw = event.dataTransfer.getData('application/x-craftara-clip');
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as { action: string; id?: string };
      if (payload.action !== 'transition' || !payload.id) return;
      applyTransitionTo(clip.id, payload.id);
    } catch {
      /* ignore a bad drag payload */
    }
  };

  const menuClip = menu ? video.clips?.find((item) => item.id === menu.clipId) : undefined;
  const menuCanSplit = Boolean(
    menuClip &&
      currentTimeMs > menuClip.startMs + VIDEO_MIN_CLIP_MS &&
      currentTimeMs < menuClip.startMs + menuClip.durationMs - VIDEO_MIN_CLIP_MS
  );

  return (
    <div
      onKeyDown={(event) => {
        if ((event.target as HTMLElement).closest('input, textarea')) return;
        if (event.key === 'Escape') {
          if (menu) setMenu(null);
          return;
        }
        const meta = event.metaKey || event.ctrlKey;
        const key = event.key.toLowerCase();
        if (meta && key === 'd' && selectedId) {
          event.preventDefault();
          duplicateSelected(selectedId);
          return;
        }
        if (meta && key === 'c' && selectedId) {
          event.preventDefault();
          const clip = video.clips?.find((item) => item.id === selectedId);
          if (clip) copyClip(clip);
          return;
        }
        if (meta && key === 'v' && copied) {
          event.preventDefault();
          pasteCopied();
          return;
        }
        if (!meta && key === 's' && selectedId) {
          event.preventDefault();
          splitSelected(selectedId);
          return;
        }
        if (event.key !== 'Delete' && event.key !== 'Backspace') return;
        if (liveGap) {
          event.preventDefault();
          useVideoStore.getState().changeTimeline((doc) => closeGap(doc, liveGap.trackId, liveGap.startMs + 1));
          setGapPick(null);
          return;
        }
        if (selectedId) {
          event.preventDefault();
          deleteSelected();
        }
      }}
      tabIndex={0}
    >
    <div className="vt-scroll" ref={scrollRef}>
      <div className="vt-track vt-clip-track" style={{ width }}>
        <TimelineRuler
          totalMs={totalMs}
          pxPerSec={pxPerSec}
          widthPx={width}
        />
        <div
          className="vt-clip-lanes"
          onClick={(event) => {
            if (suppressSeek.current) {
              suppressSeek.current = false;
              return;
            }
            if ((event.target as HTMLElement).closest('.vt-clip, .vt-clip-delete, .vt-clip-more, .vt-gap, .vt-gap-delete, .vt-clip-menu')) return;
            setGapPick(null);
            const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
            const ms = Math.max(0, ((event.clientX - bounds.left) / pxPerSec) * 1000);
            useVideoStore.getState().seek(ms);
          }}
        >
          {tracks.map((track) => (
            <div
              key={track.id}
              className={`vt-lane vt-lane-${track.kind}`}
              data-track-id={track.id}
              style={{ height: LANE_H }}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('.vt-clip, .vt-clip-delete, .vt-clip-more, .vt-gap-delete, .vt-clip-menu')) return;
                if (track.locked) return;
                const bounds = event.currentTarget.getBoundingClientRect();
                const ms = Math.max(0, ((event.clientX - bounds.left) / pxPerSec) * 1000);
                const gap = gapAt(video.clips ?? [], track.id, ms);
                if (!gap) return;
                event.stopPropagation();
                useVideoStore.getState().selectClip(null);
                setGapPick({ trackId: track.id, ms });
              }}
              onDrop={(event) => {
                event.preventDefault();
                const raw = event.dataTransfer.getData('application/x-craftara-clip');
                if (!raw) return;
                try {
                  const payload = JSON.parse(raw) as { action: string; id?: string; text?: string };
                  const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
                  const ms = Math.max(0, ((event.clientX - bounds.left) / pxPerSec) * 1000);
                  if (payload.action === 'transition' && payload.id) {
                    const onTrack = (video.clips ?? [])
                      .filter((clip) => clip.trackId === track.id && clip.kind !== 'audio')
                      .sort((a, b) => a.startMs - b.startMs);
                    const hit =
                      onTrack.find((clip) => ms >= clip.startMs && ms < clip.startMs + clip.durationMs) ??
                      onTrack[onTrack.length - 1];
                    if (!hit) {
                      useVideoStore.getState().setNotice('Drop the transition on a clip that has another clip before it.');
                      return;
                    }
                    applyTransitionTo(hit.id, payload.id);
                    return;
                  }
                  window.dispatchEvent(new CustomEvent('craftara:drop-clip', {
                    detail: { ...payload, trackId: track.id, atMs: ms },
                  }));
                } catch {
                  /* ignore a bad drag payload */
                }
              }}
            >
              <span className="vt-lane-name">{track.name}</span>
              {liveGap && liveGap.trackId === track.id ? (
                <div
                  className="vt-gap"
                  style={{
                    left: (liveGap.startMs / 1000) * pxPerSec,
                    width: ((liveGap.endMs - liveGap.startMs) / 1000) * pxPerSec,
                  }}
                >
                  <button
                    type="button"
                    className="vt-gap-delete"
                    aria-label="Delete this gap"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      useVideoStore.getState().changeTimeline((doc) => closeGap(doc, liveGap.trackId, liveGap.startMs + 1));
                      setGapPick(null);
                    }}
                  >
                    <TrashIcon />
                    <span className="vt-delete-tip">Delete this gap</span>
                  </button>
                </div>
              ) : null}
              {video.clips
                ?.filter((clip) => clip.trackId === track.id)
                .map((clip) => {
                  const shown = display(clip);
                  const tone = clip.kind === 'audio' ? 'audio' : clip.kind === 'text' || clip.kind === 'caption' ? 'text' : 'visual';
                  return (
                    <button
                      key={clip.id}
                      type="button"
                      draggable={false}
                      className={`vt-clip vt-clip-${tone} ${selectedId === clip.id ? 'active' : ''} ${clip.transitionIn && clip.transitionIn.id !== 'cut' && clip.transitionIn.id !== 'none' ? 'has-transition' : ''}`}
                      style={{
                        left: (shown.startMs / 1000) * pxPerSec,
                        width: Math.max(12, (shown.durationMs / 1000) * pxPerSec),
                      }}
                      onPointerDown={(event) => beginDrag(event, clip)}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openMenu(event.clientX, event.clientY, clip);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDrop={(event) => onClipDrop(event, clip)}
                    >
                      <span
                        className="vt-clip-edge start"
                        aria-hidden="true"
                        onPointerDown={(event) => beginDrag(event, clip, 'start')}
                      />
                      <span className="vt-clip-label">
                        {clip.kind === 'audio' ? '♪ ' : ''}
                        {clip.name}
                      </span>
                      <span
                        className="vt-clip-edge end"
                        aria-hidden="true"
                        onPointerDown={(event) => beginDrag(event, clip, 'end')}
                      />
                    </button>
                  );
                })}
              {video.clips
                ?.filter((clip) => clip.trackId === track.id && clip.id === selectedId && !drag)
                .flatMap((clip) => {
                  const clipW = Math.max(12, (clip.durationMs / 1000) * pxPerSec);
                  if (clipW < 72) return [];
                  const center = (clip.startMs / 1000) * pxPerSec + clipW / 2;
                  return [
                    <button
                      key={`${clip.id}-delete`}
                      type="button"
                      className="vt-clip-delete"
                      aria-label={`Delete ${clip.name}`}
                      style={{ left: center }}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteSelected();
                      }}
                    >
                      <TrashIcon />
                      <span className="vt-delete-tip">Delete</span>
                    </button>,
                    <button
                      key={`${clip.id}-more`}
                      type="button"
                      className="vt-clip-more"
                      aria-label={`Options for ${clip.name}`}
                      aria-haspopup="menu"
                      style={{ left: center }}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        const rect = event.currentTarget.getBoundingClientRect();
                        openMenu(rect.left, rect.bottom + 8, clip);
                      }}
                    >
                      <MoreIcon />
                    </button>,
                  ];
                })}
            </div>
          ))}
          <Playhead x={playheadX} />
        </div>
      </div>
    </div>
      <div className="vt-clip-tools">
        <span className="vt-timecode">{formatTimecode(currentTimeMs)} / {formatTimecode(video.totalDurationMs)}</span>
        <button
          type="button"
          className="vt-btn"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            splitSelected(selectedId);
          }}
        >
          Split
        </button>
        <button
          type="button"
          className="vt-btn"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            useVideoStore.getState().changeTimeline((doc) => ungroupClip(doc, selectedId));
          }}
        >
          Ungroup
        </button>
        <button
          type="button"
          className="vt-btn"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            deleteSelected();
          }}
        >
          Delete
        </button>
        <span className="vt-sr">{TRACK.visual}</span>
      </div>
      {menu && menuClip ? (
        <ClipMenu
          x={menu.x}
          y={menu.y}
          panel={menu.panel}
          clip={menuClip}
          canPaste={Boolean(copied)}
          canSplit={menuCanSplit}
          onClose={() => setMenu(null)}
          onPanel={(panel) => setMenu({ ...menu, panel })}
          onDuplicate={() => duplicateSelected(menuClip.id)}
          onCopy={() => copyClip(menuClip)}
          onPaste={pasteCopied}
          onDelete={deleteSelected}
          onSplit={() => splitSelected(menuClip.id)}
          onRename={(name) => {
            useVideoStore.getState().changeTimeline((doc) => renameClip(doc, menuClip.id, name));
            setMenu(null);
          }}
          onDuration={(seconds) => {
            const ms = Math.round(Number(seconds) * 1000);
            if (!Number.isFinite(ms)) return;
            useVideoStore.getState().changeTimeline((doc) => setClipDuration(doc, menuClip.id, ms));
            setMenu(null);
          }}
        />
      ) : null}
    </div>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6 2h4l.5 1H13v1.5H3V3h2.5L6 2zm-1.2 3h6.4l-.6 8.2a1 1 0 0 1-1 .8H6.4a1 1 0 0 1-1-.8L4.8 5z"
      />
    </svg>
  );
}

function MoreIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <circle cx="3.5" cy="8" r="1.3" fill="currentColor" />
      <circle cx="8" cy="8" r="1.3" fill="currentColor" />
      <circle cx="12.5" cy="8" r="1.3" fill="currentColor" />
    </svg>
  );
}

function MenuGlyph({ d }: { d: string }): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClipMenu({
  x,
  y,
  panel,
  clip,
  canPaste,
  canSplit,
  onClose,
  onPanel,
  onDuplicate,
  onCopy,
  onPaste,
  onDelete,
  onSplit,
  onRename,
  onDuration,
}: {
  x: number;
  y: number;
  panel: ClipMenuState['panel'];
  clip: TimelineClip;
  canPaste: boolean;
  canSplit: boolean;
  onClose: () => void;
  onPanel: (panel: ClipMenuState['panel']) => void;
  onDuplicate: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onSplit: () => void;
  onRename: (name: string) => void;
  onDuration: (seconds: string) => void;
}): JSX.Element {
  return (
    <div
      className="vt-clip-menu"
      role={panel === 'actions' ? 'menu' : 'dialog'}
      aria-label={panel === 'actions' ? `${clip.name} options` : panel === 'rename' ? 'Rename clip' : 'Edit duration'}
      style={{ left: x, top: y }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {panel === 'rename' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get('name');
            if (typeof value === 'string') onRename(value);
          }}
        >
          <label htmlFor="vt-clip-rename">Rename</label>
          <input id="vt-clip-rename" name="name" defaultValue={clip.name} autoFocus maxLength={80} />
          <div className="vt-clip-menu-actions">
            <button type="button" onClick={() => onPanel('actions')}>Back</button>
            <button type="submit">Save</button>
          </div>
        </form>
      ) : null}
      {panel === 'duration' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get('seconds');
            if (typeof value === 'string') onDuration(value);
          }}
        >
          <label htmlFor="vt-clip-duration">Duration in seconds</label>
          <input
            id="vt-clip-duration"
            name="seconds"
            inputMode="decimal"
            defaultValue={(clip.durationMs / 1000).toFixed(1)}
            autoFocus
          />
          <div className="vt-clip-menu-actions">
            <button type="button" onClick={() => onPanel('actions')}>Back</button>
            <button type="submit">Apply</button>
          </div>
        </form>
      ) : null}
      {panel === 'actions' ? (
        <>
          <MenuRow icon="M8 3v4M6 5h4M4 8h8v5H4z" label="Duplicate" shortcut="Ctrl+D" onClick={onDuplicate} />
          <MenuRow icon="M6 3h6v3H6zM4 6h8v7H4z" label="Copy" shortcut="Ctrl+C" onClick={onCopy} />
          <MenuRow icon="M5 3h6l2 2v8H5zM7 3v3h4" label="Paste" shortcut="Ctrl+V" disabled={!canPaste} onClick={onPaste} />
          <MenuRow icon="M6 3h4M4 5h8M5 5l1 8h4l1-8" label="Delete" shortcut="Del" onClick={onDelete} />
          <hr />
          <MenuRow icon="M4 3v10M12 3v10M4 8h8" label="Split" shortcut="S" disabled={!canSplit} onClick={onSplit} />
          <MenuRow icon="M8 3a5 5 0 1 1 0 10A5 5 0 0 1 8 3zM8 5v3l2 1" label="Edit duration" onClick={() => onPanel('duration')} />
          <MenuRow icon="M3 13h10M5 13V5h6v8" label="Rename" onClick={() => onPanel('rename')} />
          <button type="button" className="vt-clip-menu-dismiss" onClick={onClose}>
            Close
          </button>
        </>
      ) : null}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  shortcut,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  shortcut?: string;
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" role="menuitem" disabled={disabled} onClick={onClick}>
      <MenuGlyph d={icon} />
      <span>{label}</span>
      {shortcut ? <kbd>{shortcut}</kbd> : null}
    </button>
  );
}
